// lib/generated-result-name.ts
//
// What to call a generated result, in the words a user would use. These names are not
// internal: they are the label on a List tile, the entry in the layer list, and what the
// chat timeline reads back. They used to be four characters of the task UUID ("Video cb33")
// or a wall-clock stamp ("Result 3:42:11 PM"), both of which read as a bug reference to
// someone who is not a developer.
//
// Lives in lib/ rather than beside its caller so it can be unit tested: kernel modules pull
// the whole application graph and do not load under jest.

/** The longest label that still reads as a name rather than a paragraph. */
const MAX_LABEL_LENGTH = 48;
/** Below this, cutting on a word boundary leaves too little to be recognisable. */
const MIN_WORD_BOUNDARY_LENGTH = 20;

export const isGenericListOutputName = (name: unknown, model: unknown): boolean => {
  const text = typeof name === 'string' ? name.trim() : '';
  return !text || text === 'New image' || (typeof model === 'string' && text === `${model} result`);
};

/**
 * First sentence of a prompt, without breaking on the dots that are not sentence ends.
 * Retail prompts are full of measurements ("a 3.5 meter neon sign") and abbreviations
 * ("e.g. Westfield"), and splitting on a bare "." turned those into "A 3".
 */
const firstSentenceOf = (prompt: string): string => {
  // A sentence end is terminal punctuation that (a) follows a real word of at least two
  // letters and (b) is followed by whitespace or the end of the string. "3.5" fails (b):
  // digits, and no space after the dot. "e.g." fails (a): the dot follows the single
  // letter "g". Both come up constantly in retail prompts.
  const match = prompt.match(/^([\s\S]*?\p{L}\p{L}[.!?])(?=\s|$)/u);
  const candidate = (match?.[1] ?? prompt).trim();
  // Punctuation-only prompts ("...") carry no meaning; fall through to the caller's default.
  return /[\p{L}\p{N}]/u.test(candidate) ? candidate : '';
};

/** Truncate on a word boundary, counting characters rather than UTF-16 units so an
 *  emoji's surrogate pair is never cut in half and persisted as a replacement char. */
const truncateLabel = (text: string): string => {
  const characters = Array.from(text);
  if (characters.length <= MAX_LABEL_LENGTH) return text;
  const cut = characters.slice(0, MAX_LABEL_LENGTH).join('');
  const lastSpace = cut.lastIndexOf(' ');
  const trimmed = (lastSpace > MIN_WORD_BOUNDARY_LENGTH ? cut.slice(0, lastSpace) : cut).trimEnd();
  return `${trimmed}…`;
};

export type GeneratedResultKind = 'video' | 'image';

/**
 * The prompt is the best description we have of what the user asked for, so it wins.
 * Without one, an upscale, an enhance or a sound pass carries no prompt, say what was done.
 */
export const describeGeneratedResult = (
  task: { params?: { prompt?: unknown } | null; model?: string | null },
  kind: GeneratedResultKind,
): string => {
  const rawPrompt = typeof task.params?.prompt === 'string' ? task.params.prompt.trim() : '';
  if (rawPrompt) {
    const sentence = firstSentenceOf(rawPrompt);
    if (sentence) return truncateLabel(sentence);
  }
  const model = typeof task.model === 'string' ? task.model.toLowerCase() : '';
  if (model.includes('upscale')) return `Upscaled ${kind}`;
  if (model.includes('enhance')) return `Enhanced ${kind}`;
  if (model.includes('sfx') || model.includes('mirelo')) return 'Sound effects';
  if (model.includes('vectorize') || model.includes('recraft')) return `Vectorized ${kind}`;
  return `New ${kind}`;
};

/**
 * Same label, made unique inside its workspace. Batches produce N results from one prompt,
 * so without this every one of them carries the same name, and the agent resolves layers by
 * name (useLayers: `find(l => l.id === identifier || l.name === identifier)`), so it would
 * silently act on whichever came first.
 */
export const uniqueGeneratedResultName = (desiredName: string, takenNames: Iterable<string>): string => {
  const taken = new Set(takenNames);
  if (!taken.has(desiredName)) return desiredName;
  for (let suffix = 2; suffix < 1000; suffix++) {
    const candidate = `${desiredName} ${suffix}`;
    if (!taken.has(candidate)) return candidate;
  }
  return desiredName;
};
